/**
 * Batch Quality Tab Component
 * Displays quality information and metrics
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-002
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BatchResponse, QualityGradeConfig } from '@/types/inventory';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CheckCircle, AlertTriangle } from 'lucide-react';

interface BatchQualityTabProps {
  batch: BatchResponse;
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

function InfoRow({ label, value, className }: InfoRowProps) {
  return (
    <div className={cn('flex justify-between items-start gap-4', className)}>
      <span className="text-sm text-gray-600 font-medium">{label}</span>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

export function BatchQualityTab({ batch }: BatchQualityTabProps) {
  const gradeConfig = QualityGradeConfig[batch.qualityGrade];
  const purityPercent = Math.min(Math.max(batch.purityPercentage, 0), 100);

  // Determine purity color based on percentage
  const getPurityColor = (purity: number) => {
    if (purity >= 95) return 'text-green-700';
    if (purity >= 85) return 'text-blue-700';
    if (purity >= 70) return 'text-yellow-700';
    return 'text-red-700';
  };

  const getPurityBgColor = (purity: number) => {
    if (purity >= 95) return 'bg-green-50 border-green-200';
    if (purity >= 85) return 'bg-blue-50 border-blue-200';
    if (purity >= 70) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="space-y-6">
      {/* Quality Grade Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            Quality Grade
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Grade {batch.qualityGrade}
              </span>
              <Badge className={cn(
                gradeConfig.bgColor,
                gradeConfig.color,
                'border-none font-semibold text-lg'
              )}>
                {gradeConfig.label}
              </Badge>
            </div>
            <p className="text-sm text-gray-600">
              {gradeConfig.description}
            </p>
          </div>

          <InfoRow
            label="Grade Range"
            value={`${gradeConfig.minPurity.toFixed(1)}% - ${gradeConfig.maxPurity.toFixed(1)}%`}
          />
        </CardContent>
      </Card>

      {/* Purity Percentage Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Purity Percentage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={cn(
            'p-4 rounded-lg border',
            getPurityBgColor(purityPercent)
          )}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Purity Level
              </span>
              <span className={cn(
                'text-2xl font-bold',
                getPurityColor(purityPercent)
              )}>
                {batch.purityPercentage.toFixed(2)}%
              </span>
            </div>
            <Progress
              value={purityPercent}
              className="h-3"
            />
          </div>

          {/* Purity Interpretation */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-600 font-medium">Minimum</div>
              <div className="text-sm font-bold text-gray-900">
                {gradeConfig.minPurity.toFixed(1)}%
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-blue-600 font-medium">Current</div>
              <div className="text-sm font-bold text-blue-900">
                {batch.purityPercentage.toFixed(2)}%
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-600 font-medium">Maximum</div>
              <div className="text-sm font-bold text-gray-900">
                {gradeConfig.maxPurity.toFixed(1)}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Information Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Test Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow
            label="Test Date"
            value={batch.createdAt ? format(new Date(batch.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A'}
          />
          <InfoRow
            label="Tested By"
            value={batch.createdBy || 'N/A'}
          />
          {batch.updatedAt && (
            <>
              <InfoRow
                label="Last Updated"
                value={format(new Date(batch.updatedAt), 'MMM dd, yyyy HH:mm')}
              />
              <InfoRow
                label="Updated By"
                value={batch.updatedBy || 'N/A'}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Quality Indicators */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quality Indicators</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Pressure (if available) */}
            {batch.pressure !== undefined && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    Pressure
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {batch.pressure} bar
                  </span>
                </div>
              </div>
            )}

            {/* Grade Status */}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-green-700">
                  Grade Status
                </span>
                <Badge className={cn(
                  gradeConfig.bgColor,
                  gradeConfig.color,
                  'border-none'
                )}>
                  {batch.qualityGrade}
                </Badge>
              </div>
            </div>

            {/* Compliance Status */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700">
                  PESO Compliant
                </span>
                {batch.pesoCompliant ? (
                  <Badge className="bg-green-100 text-green-800 border-none">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Yes
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    No
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quality Remarks (if any) */}
      {batch.qualityGrade === 'C' && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-lg text-yellow-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Quality Notice
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-yellow-700">
              This batch has been assigned Grade C quality. While still usable, it may have
              reduced performance characteristics. Please handle with appropriate care.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
